/**
 * The shared nav + footer + theme handling for the standalone brand pages.
 *
 * Keeps /plugin-download/ and /contact/ visually identical to the flagship and plugin
 * pages without either of them re-implementing the wordmark, the light/dark toggle, or
 * the footer. The theme choice is stored under the same key the other pages use, so
 * switching to dark on one page keeps you in dark everywhere.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../constants/routes";
import logoDark from "../../assets/brand/logo_site.svg";
import logoLight from "../../assets/brand/logo_site_onlight.svg";
import { BrandTheme, BRAND_THEME_KEY } from "./brandPageCss";
import { LEGAL_FOOTER_OWNER, LEGAL_TRADEMARKS } from "../legal/legalConstants";

export const SunSvg: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth={1.7} strokeLinecap="round">
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4 12H2M22 12h-2M5 5l1.5 1.5M17.5 17.5L19 19M19 5l-1.5 1.5M6.5 17.5L5 19" />
  </svg>
);

export const MoonSvg: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
  </svg>
);

export const DownloadSvg: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3v12M7 11l5 5 5-5M4 20h16" />
  </svg>
);

export const MailSvg: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M3.5 7l8.5 6 8.5-6" />
  </svg>
);

/** Remembers the light/dark choice across every brand page. */
export function useBrandTheme(): [BrandTheme, () => void] {
  const [theme, setTheme] = useState<BrandTheme>("light");

  useEffect(() => {
    try {
      const stored = localStorage.getItem(BRAND_THEME_KEY);
      if (stored === "light" || stored === "dark") setTheme(stored);
    } catch (e) {
      /* ignore */
    }
  }, []);

  const toggle = () =>
    setTheme((t) => {
      const next: BrandTheme = t === "dark" ? "light" : "dark";
      try {
        localStorage.setItem(BRAND_THEME_KEY, next);
      } catch (e) {
        /* ignore */
      }
      return next;
    });

  return [theme, toggle];
}

interface BrandNavProps {
  theme: BrandTheme;
  onToggleTheme: () => void;
  /** Extra links shown left of the theme toggle. */
  children?: React.ReactNode;
}

export const BrandNav: React.FC<BrandNavProps> = ({ theme, onToggleTheme, children }) => {
  const navigate = useNavigate();
  const logo = theme === "light" ? logoLight : logoDark;

  return (
    <nav>
      <div className="nav-in">
        <button
          className="brand-btn"
          onClick={() => navigate(ROUTES.HOME)}
          aria-label="CherryClipz home"
        >
          <img className="cherry-mark" src={logo} alt="" height={30} style={{ width: "auto" }} />
          <span className="wordmark">
            Cherry<span className="ck">Clipz</span>
          </span>
        </button>
        <div className="nav-right">
          {children}
          <button
            className="toggle"
            onClick={onToggleTheme}
            aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
            title="Light / dark"
          >
            <span className="sun">
              <SunSvg />
            </span>
            <span className="moon">
              <MoonSvg />
            </span>
          </button>
        </div>
      </div>
    </nav>
  );
};

export const BrandFooter: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <footer>
    <div className="wrap foot">
      <span>{LEGAL_FOOTER_OWNER}</span>
      <div className="fl">{children}</div>
      <p className="tm">{LEGAL_TRADEMARKS}</p>
    </div>
  </footer>
);
